/**
 * Carrying one cell's edit across the rest of the plan.
 *
 * A weekly plan is 52 columns wide, so a single real-world change — losing doors at an account —
 * otherwise costs 52 identical edits. Fill-forward writes the edited period and every later one
 * on the same row, stopping short of anything already settled.
 */

import { annual_growth_factor } from "./data_generator"
import type { QuantityBasis } from "./quantity_basis"

/** What a fill-forward needs to know about one period on the row being edited. */
export interface FillForwardPoint {
  period_key: string
  store_count?: number
  is_actual?: boolean
  is_locked?: boolean
}

/** A period and the value to write there. */
export interface PeriodValue {
  period_key: string
  value: number
}

/**
 * Periods a fill-forward should write, in plan order, starting at `from_period_key`.
 *
 * Later periods that carry an actual or are locked are skipped: a plan change applies to what is
 * still being planned, never back over what already happened. The edited period is always
 * included — the user is editing it, so it is editable by definition — and periods the row has no
 * cell for are left out so callers can report an honest count.
 */
export function fill_forward_periods(
  ordered_period_keys: string[],
  points: FillForwardPoint[],
  from_period_key: string,
): FillForwardPoint[] {
  const by_key = new Map(points.map((point) => [point.period_key, point]))
  const start_index = ordered_period_keys.indexOf(from_period_key)
  if (start_index === -1) {
    const only = by_key.get(from_period_key)
    return only ? [only] : []
  }

  const selected: FillForwardPoint[] = []
  for (let index = start_index; index < ordered_period_keys.length; index++) {
    const point = by_key.get(ordered_period_keys[index])
    if (!point) continue
    if (index > start_index && (point.is_actual || point.is_locked)) continue
    selected.push(point)
  }
  return selected
}

/**
 * The value to write in each period when carrying an edit forward.
 *
 * With no `velocity`, the same number repeats — a door count is 480 stores every week. With one,
 * each period re-derives its quantity from its own door count, because carrying a velocity forward
 * means holding units-per-store-per-week constant, not holding units constant: the same UPSPW
 * against a shrunken store count is deliberately a smaller number.
 */
export function fill_forward_values(
  points: FillForwardPoint[],
  value: number,
  velocity?: number,
): PeriodValue[] {
  if (velocity == null || !Number.isFinite(velocity)) {
    return points.map((point) => ({ period_key: point.period_key, value }))
  }
  return points.map((point) => ({
    period_key: point.period_key,
    value: Math.round(velocity * (point.store_count ?? 0)),
  }))
}

/** A row's growth settings, as the add-source dialog and the growth column hold them (per year). */
export interface RowGrowthRules {
  /** Store-count growth per year (0.02 = +2% doors after twelve months). */
  store_growth: number
  /** Same-store (or account) growth per year. */
  same_store_growth: number
  /** Whether the row's cases are one store's rate or the account's total. */
  quantity_basis: QuantityBasis | null
}

/** The edited measures "apply to the rest of the months" knows how to grow. */
export type GrowableField = "store_count" | "cases_sold" | "units_sold" | "volume_dollars"

/**
 * "Apply these changes to the rest of the months?" (1st Phorm, 10/29): the value typed into one
 * month becomes the base the row's own growth settings run from — the same rule a new source is
 * generated with (`annual_growth_factor`, per year, compounded monthly). Doors grow at the
 * store-growth rate; a per-store case rate at the same-store rate; an account's total cases and
 * revenue at both. `points` is what `fill_forward_periods` selected (so actual and locked months
 * are already out); the edited month itself is not returned.
 */
export function grow_forward_values(
  ordered_period_keys: string[],
  points: FillForwardPoint[],
  from_period_key: string,
  field: GrowableField,
  value: number,
  rules: RowGrowthRules,
  months_per_period: number = 1,
): PeriodValue[] {
  const start = ordered_period_keys.indexOf(from_period_key)
  if (start === -1) return []
  const out: PeriodValue[] = []
  for (const point of points) {
    const index = ordered_period_keys.indexOf(point.period_key)
    if (index <= start) continue
    const months = (index - start) * months_per_period
    const store_factor = annual_growth_factor(rules.store_growth, months)
    const same_store_factor = annual_growth_factor(rules.same_store_growth, months)
    if (field === "store_count") {
      out.push({ period_key: point.period_key, value: Math.round(value * store_factor) })
    } else if (field === "volume_dollars") {
      out.push({ period_key: point.period_key, value: Math.round(value * store_factor * same_store_factor * 100) / 100 })
    } else {
      const factor = rules.quantity_basis === "total" ? store_factor * same_store_factor : same_store_factor
      const grown = value * factor
      out.push({
        period_key: point.period_key,
        value: field === "units_sold" ? Math.round(grown) : Math.round(grown * 100) / 100,
      })
    }
  }
  return out
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest
  const keys = ["2027-01", "2027-02", "2027-03", "2027-04"]
  const points: FillForwardPoint[] = keys.map((period_key: string) => ({ period_key, store_count: 100 }))

  describe("grow_forward_values", () => {
    it("grows a January door count at the store-growth rate, per year compounded monthly", () => {
      const out = grow_forward_values(keys, points, "2027-01", "store_count", 1200, {
        store_growth: 0.12,
        same_store_growth: 0.5,
        quantity_basis: "per_store",
      })
      expect(out.map((p: PeriodValue) => p.period_key)).toEqual(["2027-02", "2027-03", "2027-04"])
      expect(out[0].value).toBe(Math.round(1200 * Math.pow(1.12, 1 / 12)))
      expect(out[2].value).toBe(Math.round(1200 * Math.pow(1.12, 3 / 12)))
    })

    it("grows a per-store case rate at the same-store rate only, and an account total at both", () => {
      const per_store = grow_forward_values(keys, points, "2027-02", "cases_sold", 10, {
        store_growth: 0.1,
        same_store_growth: 0.1,
        quantity_basis: "per_store",
      })
      expect(per_store[0]).toEqual({ period_key: "2027-03", value: Math.round(10 * Math.pow(1.1, 1 / 12) * 100) / 100 })
      const total = grow_forward_values(keys, points, "2027-02", "cases_sold", 10, {
        store_growth: 0.1,
        same_store_growth: 0.1,
        quantity_basis: "total",
      })
      expect(total[0].value).toBe(Math.round(10 * Math.pow(1.1, 2 / 12) * 100) / 100)
    })

    it("holds the value flat when the row has no growth, and skips months fill-forward left out", () => {
      const only_april = [points[0], points[3]]
      const out = grow_forward_values(keys, only_april, "2027-01", "volume_dollars", 500, {
        store_growth: 0,
        same_store_growth: 0,
        quantity_basis: null,
      })
      expect(out).toEqual([{ period_key: "2027-04", value: 500 }])
    })
  })
}
